/**
 * Station hero photo resolver.
 *
 * Single source of truth for "what image do we show for this
 * station?" Two screens consume this today — the map popup +
 * bottom sheet (`MapPage.tsx`, via `getCardBackground`) and the
 * station detail page (`StationDetailPage.tsx`). Keeping the
 * fallback chain here means a station blank-photo fix lands once
 * and both surfaces pick it up.
 *
 * Resolution order (matches MapPage's documented chain):
 *   1. Operator-supplied `photo_url` when non-empty.
 *   2. Google Street View Static API at the station's lat/lng,
 *      keyed off the same `VITE_GOOGLE_MAPS_KEY` already required
 *      by the map. No extra config; falls through silently when
 *      the key isn't set or the station has no coordinates.
 *   3. Null — caller decides what placeholder to render. The
 *      map uses a CSS gradient data-URI; the detail page uses
 *      its own gradient placeholder. Returning null instead of
 *      a placeholder URL keeps this helper presentation-free.
 *
 * Returns a plain URL (no CSS `url(...)` wrapping) so callers
 * can drop it into either an `<img src>` (PhotoHero) or a CSS
 * `background-image: url('${x}')` (map cards).
 *
 * The Street View tier returns Google's "no imagery available"
 * placeholder when coverage is missing for a given lat/lng. That's
 * degraded but still visually distinct from the gradient — and
 * over time most addresses will have coverage. If a tenant flags
 * a specific station as photo-bad, they can paste a real
 * `photo_url` to override the chain at tier 1.
 */

const GOOGLE_MAPS_KEY = import.meta.env['VITE_GOOGLE_MAPS_KEY'] as string | undefined;

export interface StationHeroInput {
  photo_url?: string | null;
  address: { coordinates?: { lat: number; lng: number } };
}

/**
 * Resolve the best hero photo URL for a station. Returns null when
 * neither the operator photo nor a Street View URL can be produced
 * (no coordinates, or the maps key is missing). Caller renders its
 * own placeholder in the null case.
 */
export function resolveStationHeroUrl(loc: StationHeroInput): string | null {
  const photo = loc.photo_url?.trim();
  if (photo) return photo;
  const coords = loc.address.coordinates;
  if (coords && GOOGLE_MAPS_KEY) {
    return `https://maps.googleapis.com/maps/api/streetview?size=640x320&location=${coords.lat},${coords.lng}&key=${encodeURIComponent(GOOGLE_MAPS_KEY)}`;
  }
  return null;
}
